
import { supabase } from './client';
import { createTablesIfNotExist } from './initialization';
import { initializeLocalFallback } from './local';

// Cache connection status to avoid redundant checks
let connectionStatus: 'unknown' | 'connected' | 'disconnected' = 'unknown';
let lastConnectionCheck = 0;
const CONNECTION_CHECK_INTERVAL = 60000; // 60 seconds (increased from 30s to reduce DB load)
const CONNECTION_TIMEOUT = 3000; // 3 seconds (reduced from 5s)

// Helper functions for working with Supabase with timeout protection
export const initializeSupabase = async () => {
  try {
    // Skip if we've already verified connection recently - increased interval
    if (connectionStatus === 'connected' && 
        Date.now() - lastConnectionCheck < CONNECTION_CHECK_INTERVAL) {
      return true;
    }
    
    // Test connection to Supabase with timeout protection and optimized approach
    const startTime = Date.now();
    
    // Use Promise.race to implement timeout
    const connectionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection timeout')), CONNECTION_TIMEOUT);
    });
    
    const { error } = await Promise.race([
      connectionPromise,
      timeoutPromise
    ]) as { error: any };
    
    if (error) {
      connectionStatus = 'disconnected';
      lastConnectionCheck = Date.now();
      return false;
    }
    
    // Only try to create tables once per session and only if needed
    // This reduces unnecessary database operations
    if (connectionStatus !== 'connected') {
      const tablesCreated = await createTablesIfNotExist();
      
      if (!tablesCreated) {
        connectionStatus = 'disconnected';
        lastConnectionCheck = Date.now();
        return false;
      }
    }
    
    connectionStatus = 'connected';
    lastConnectionCheck = Date.now();
    return true;
  } catch (error) {
    connectionStatus = 'disconnected';
    lastConnectionCheck = Date.now();
    
    // Initialize local fallback when Supabase fails
    try {
      await initializeLocalFallback();
    } catch (fallbackError) {
      // Continue even if fallback fails
    }
    
    return false;
  }
};

// General helper function to check if Supabase is available with improved reliability
export const isSupabaseAvailable = async (): Promise<boolean> => {
  // Use cached status if recent - increased cache duration
  if (connectionStatus !== 'unknown' && 
      Date.now() - lastConnectionCheck < CONNECTION_CHECK_INTERVAL) {
    return connectionStatus === 'connected';
  }
  
  try {
    // Try with shorter timeout to avoid blocking
    const timeoutPromise = new Promise<boolean>((_, reject) => {
      setTimeout(() => reject(new Error('Connection timeout')), CONNECTION_TIMEOUT);
    });
    
    // Simplified availability check
    const checkPromise = new Promise<boolean>(async (resolve) => {
      try {
        const { error } = await supabase.auth.getSession();
        resolve(!error);
      } catch (err) {
        resolve(false);
      }
    });
    
    // Race between timeout and actual check
    const result = await Promise.race([checkPromise, timeoutPromise]);
    
    connectionStatus = result ? 'connected' : 'disconnected';
    lastConnectionCheck = Date.now();
    
    return result as boolean;
  } catch (error) {
    connectionStatus = 'disconnected';
    lastConnectionCheck = Date.now();
    return false;
  }
};

// Reset connection status - for testing or forced refresh
export const resetConnectionStatus = () => {
  connectionStatus = 'unknown';
  lastConnectionCheck = 0;
};
